import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import dayjs from 'dayjs';

import ApprovalStatusChip from 'components/table/ApprovalStatusChip';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import { orderStatusChipSx, paymentStatusChipSx } from 'components/table/tableChipStyles';
import { getRefundRequestDetail } from 'services/payment.service';
import { useToast } from 'contexts/ToastContext';
import { brand } from 'utils/designTokens';

export default function RefundRequestDetailsDialog({
  open,
  requestId,
  onClose
}) {
  const { showApiError } = useToast();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && requestId) {
      loadDetail(requestId);
    } else {
      setDetail(null);
    }
  }, [open, requestId]);

  const loadDetail = async (id) => {
    setLoading(true);
    try {
      const res = await getRefundRequestDetail(id);
      if (res?.data) {
        setDetail(res.data);
      }
    } catch (err) {
      showApiError(err, 'Failed to load refund request details.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ReceiptLongOutlinedIcon color="primary" />
            <Typography variant="h5" fontWeight={700}>
              Refund Request Details
            </Typography>
          </Stack>
          {detail?.status && <ApprovalStatusChip status={detail.status} />}
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2.5, pb: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={36} />
          </Box>
        ) : !detail ? (
          <Typography color="text.secondary" align="center" py={4}>
            No details available.
          </Typography>
        ) : (
          <Stack spacing={3}>
            {/* Top Financial Breakdown Cards */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Requested Refund
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount value={detail.amount} variant="h5" fontWeight={700} color="primary.main" />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Total Order Paid
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount value={detail.paid_amount} variant="h5" fontWeight={700} />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: brand.canvas, borderColor: brand.border }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Previously Refunded
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount value={detail.previously_refunded_amount || 0} variant="h5" fontWeight={700} />
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, sm: 3 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: brand.canvas,
                    borderColor: brand.border
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Remaining Refundable
                  </Typography>
                  <Box mt={0.5}>
                    <CurrencyAmount
                      value={detail.remaining_refundable_amount || 0}
                      variant="h5"
                      fontWeight={700}
                      color="#2e7d32"
                    />
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Order & Customer Metadata */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2, borderColor: brand.border, height: '100%' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                    Order Details
                  </Typography>
                  <Stack spacing={1.2} mt={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Order Number:
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {detail.order?.order_no || `#${detail.order_id}`}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Order Status:
                      </Typography>
                      {detail.order?.order_status ? (
                        <Chip
                          label={detail.order.order_status}
                          size="small"
                          sx={orderStatusChipSx(detail.order.order_status)}
                        />
                      ) : (
                        '—'
                      )}
                    </Stack>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Payment Status:
                      </Typography>
                      {detail.order?.payment_status ? (
                        <Chip
                          label={detail.order.payment_status}
                          size="small"
                          sx={paymentStatusChipSx(detail.order.payment_status)}
                        />
                      ) : (
                        '—'
                      )}
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Payment Method:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {detail.order?.paymentMethod?.name ||
                          (detail.paymentTransaction?.gateway === 'stripe' ? 'Card / Stripe' : 'Cash')}
                      </Typography>
                    </Stack>

                    {detail.paymentTransaction?.stripe_charge_id && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          Stripe Charge:
                        </Typography>
                        <Typography variant="caption" fontFamily="monospace">
                          {detail.paymentTransaction.stripe_charge_id}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  variant="outlined"
                  sx={{ p: 2.5, borderRadius: 2, borderColor: brand.border, height: '100%' }}
                >
                  <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                    Customer Details
                  </Typography>
                  <Stack spacing={1.2} mt={1.5}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Name:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {detail.customer?.name || '—'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Mobile:
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {detail.customer?.mobile_number || '—'}
                      </Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Email:
                      </Typography>
                      <Typography variant="body2">{detail.customer?.email || '—'}</Typography>
                    </Stack>

                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Requested Date:
                      </Typography>
                      <Typography variant="caption">
                        {detail.createdAt ? dayjs(detail.createdAt).format('YYYY-MM-DD HH:mm') : '—'}
                      </Typography>
                    </Stack>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>

            {/* Refund Reason & Notes */}
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderColor: brand.border }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main" gutterBottom>
                Refund Request Information
              </Typography>
              <Grid container spacing={2} mt={0.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Reason Stated by Customer
                  </Typography>
                  <Typography variant="body2" fontWeight={500} mt={0.5}>
                    {detail.reason || 'None specified'}
                  </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                    Customer Note
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {detail.customer_note || '—'}
                  </Typography>
                </Grid>
              </Grid>

              {/* Review Audit Info (if already reviewed) */}
              {detail.status !== 'pending' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={700} color="text.primary" gutterBottom>
                    Review Decision & Audit
                  </Typography>
                  <Grid container spacing={2} mt={0.5}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        Reviewed By
                      </Typography>
                      <Typography variant="body2" fontWeight={600} mt={0.5}>
                        {detail.reviewer?.name || detail.reviewer?.username || 'Admin'}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        Reviewed At
                      </Typography>
                      <Typography variant="caption" mt={0.5} display="block">
                        {detail.reviewed_at ? dayjs(detail.reviewed_at).format('YYYY-MM-DD HH:mm:ss') : '—'}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        Stripe Refund ID
                      </Typography>
                      <Typography variant="caption" fontFamily="monospace" mt={0.5} display="block">
                        {detail.stripe_refund_id || '—'}
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
                        Admin Note
                      </Typography>
                      <Typography variant="body2" mt={0.5} color="text.primary">
                        {detail.admin_note || '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                </>
              )}
            </Paper>

            {/* Refund Audit History Table */}
            {Array.isArray(detail.refund_history) && detail.refund_history.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={700} color="text.primary" mb={1}>
                  Order Refund History ({detail.refund_history.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: brand.canvas }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Note</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.refund_history.map((h, i) => (
                        <TableRow key={h.id || i}>
                          <TableCell>
                            <Chip
                              label={h.payment_status}
                              size="small"
                              sx={paymentStatusChipSx(h.payment_status)}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <CurrencyAmount value={h.amount} fontWeight={600} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {h.note || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {h.timestamp ? dayjs(h.timestamp).format('YYYY-MM-DD HH:mm') : '—'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} color="inherit">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

RefundRequestDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  requestId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func.isRequired
};
